import type { ReactNode } from "react";

import { cn } from "@/lib/cn";
import { formatNumber } from "@/lib/format";

/** A small labelled figure on a tinted tile (Entry / Stop-loss / Target / Now). */
export function LevelBox({
  label,
  children,
  tone,
  sub,
  size = "md",
}: {
  label: ReactNode;
  children: ReactNode;
  tone?: "up" | "down";
  /** A muted line under the figure ("23 Oct", "1 lot"). */
  sub?: ReactNode;
  /** "lg" for the roomier call cards. */
  size?: "md" | "lg";
}) {
  return (
    <div
      className={cn(
        "min-w-0 rounded-[10px] bg-surface-2",
        size === "lg" ? "px-3 py-2" : "px-2.5 py-2",
      )}
    >
      <div
        className={cn(
          "truncate text-muted",
          size === "lg" ? "text-2xs" : "text-[11px]",
        )}
      >
        {label}
      </div>
      <div
        className={cn(
          "num break-words",
          size === "lg" ? "text-[15px] font-semibold" : "text-sm font-bold",
          tone === "up" ? "text-up" : tone === "down" ? "text-down" : "text-fg",
        )}
      >
        {children}
      </div>
      {sub ? (
        <div className="num truncate text-2xs text-subtle">{sub}</div>
      ) : null}
    </div>
  );
}

/**
 * Where a price sits between the stop (left) and the target (right), with a
 * tick at the entry. Works for both sides: a SELL's target is below its stop.
 * `outcome` colours the dot once the call has ended.
 */
export function RangeBar({
  stop,
  target,
  entry,
  now,
  outcome,
  targetLabel = "target",
  className,
}: {
  stop: number | null | undefined;
  target: number | null | undefined;
  entry?: number | null;
  now?: number | null;
  outcome?: "target_hit" | "stop_hit" | null;
  /** The right end's word: "target", or a reference mark such as "+70% (2R)". */
  targetLabel?: string;
  className?: string;
}) {
  if (stop == null || target == null || stop === target) return null;
  const pos = (v: number) =>
    Math.max(0, Math.min(100, ((v - stop) / (target - stop)) * 100));
  const at = now != null ? pos(now) : null;
  const tone =
    outcome === "target_hit"
      ? "bg-up"
      : outcome === "stop_hit"
        ? "bg-down"
        : at == null
          ? "bg-brand"
          : at >= (entry != null ? pos(entry) : 50)
            ? "bg-up"
            : "bg-down";
  return (
    <div
      className={cn("mt-4", className)}
      role="img"
      aria-label={`Price ${now != null ? `${formatNumber(now)} ` : ""}between stop ${formatNumber(stop)} and target ${formatNumber(target)}`}
    >
      <div className="relative h-1.5 rounded-full bg-[linear-gradient(90deg,var(--down-soft),var(--surface-3)_50%,var(--up-soft))]">
        {entry != null ? (
          <span
            aria-hidden
            className="absolute top-1/2 h-3 w-px -translate-y-1/2 bg-border-strong"
            style={{ left: `${pos(entry)}%` }}
          />
        ) : null}
        {at != null ? (
          <span
            aria-hidden
            className={cn(
              "absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-surface",
              tone,
            )}
            style={{ left: `${at}%` }}
          />
        ) : null}
      </div>
      <div className="relative mt-1 h-4 text-2xs text-subtle">
        <span className="absolute left-0">stop</span>
        {entry != null ? (
          <span
            className="absolute -translate-x-1/2"
            style={{ left: `${Math.max(18, Math.min(82, pos(entry)))}%` }}
          >
            entry
          </span>
        ) : null}
        <span className="absolute right-0">{targetLabel}</span>
      </div>
    </div>
  );
}
